import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Mail, Loader2, LogOut, ShoppingBag, RotateCcw, Star, Check, Repeat, X, Play, Gift, Copy, Users } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { useCart, formatAUD } from '@/context/CartContext';
import Seo from '@/components/Seo';

const requestMagic = (email) =>
  apiClient.post('/customer/auth/request', { email }).then((r) => r.data);
const verifyToken = (token) =>
  apiClient.post('/customer/auth/verify', { token }).then((r) => r.data);
const fetchMe = () => apiClient.get('/customer/me').then((r) => r.data);
const fetchOrders = () => apiClient.get('/customer/orders').then((r) => r.data);
const fetchSubs = () => apiClient.get('/customer/subscriptions').then((r) => r.data);
const fetchReferral = () => apiClient.get('/customer/referral').then((r) => r.data);
const cancelSub = (id) => apiClient.post(`/customer/subscriptions/${id}/cancel`).then((r) => r.data);
const resumeSub = (id) => apiClient.post(`/customer/subscriptions/${id}/resume`).then((r) => r.data);
const logout = () => apiClient.post('/customer/auth/logout').then((r) => r.data);

export default function Account() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [referral, setReferral] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | anonymous | authed | verifying
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      const token = params.get('token');
      if (token) {
        setStatus('verifying');
        try {
          await verifyToken(token);
          setParams({}, { replace: true });
        } catch (e) {
          if (!cancelled) setError(e?.response?.data?.detail || 'Invalid or expired link');
        }
      }
      try {
        const meData = await fetchMe();
        if (cancelled) return;
        setMe(meData);
        const [os, subs, ref] = await Promise.all([
          fetchOrders(),
          fetchSubs().catch(() => []),
          fetchReferral().catch(() => null),
        ]);
        if (cancelled) return;
        setOrders(os);
        setSubscriptions(subs || []);
        setReferral(ref);
        setStatus('authed');
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    };
    boot();
    return () => { cancelled = true; };
  }, []);

  const onRequest = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await requestMagic(email);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send link');
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    setMe(null); setOrders([]); setSubscriptions([]); setReferral(null); setStatus('anonymous'); setSent(false); setEmail('');
    navigate('/account', { replace: true });
  };

  const onCancelSub = async (id) => {
    try {
      const updated = await cancelSub(id);
      setSubscriptions((subs) =>
        subs.map((s) => (s.subscription_id === id
          ? { ...s, ...updated }
          : s))
      );
      toast.success('Subscription set to cancel at period end');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    }
  };

  const onResumeSub = async (id) => {
    try {
      const updated = await resumeSub(id);
      setSubscriptions((subs) =>
        subs.map((s) => (s.subscription_id === id
          ? { ...s, ...updated }
          : s))
      );
      toast.success('Subscription resumed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resume');
    }
  };

  return (
    <div className="section-light min-h-screen bg-[var(--mw-bg)] text-white">
      <Seo title="My Account" description="Sign in to your Meat Works account to view orders, subscriptions and referral rewards." path="/account" noindex />
      <header className="border-b border-white/10 px-6 py-5">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-[10px] uppercase tracking-[0.32em] text-white/60 hover:text-white">
            ← Back to shop
          </Link>
          {status === 'authed' && (
            <button
              type="button"
              onClick={onLogout}
              data-testid="account-logout"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-[var(--mw-primary)] transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-14">
        {status === 'loading' || status === 'verifying' ? (
          <LoadingBlock label={status === 'verifying' ? 'Signing you in…' : 'Loading…'} />
        ) : status === 'anonymous' ? (
          <AnonView
            email={email}
            setEmail={setEmail}
            onRequest={onRequest}
            busy={busy}
            sent={sent}
            error={error}
          />
        ) : (
          <AuthedView
            me={me}
            orders={orders}
            subscriptions={subscriptions}
            referral={referral}
            onCancelSub={onCancelSub}
            onResumeSub={onResumeSub}
          />
        )}
      </main>
    </div>
  );
}

function LoadingBlock({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <Loader2 size={32} className="animate-spin text-white/50" />
      <div className="text-[11px] uppercase tracking-[0.28em] text-white/55">{label}</div>
    </div>
  );
}

function AnonView({ email, setEmail, onRequest, busy, sent, error }) {
  if (sent) {
    return (
      <div data-testid="account-check-email" className="max-w-md mx-auto text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-6">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.32em] text-emerald-400 mb-3">Sent</div>
        <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
          Check your inbox.
        </h1>
        <p className="text-white/60 leading-relaxed">
          We just emailed <span className="text-white">{email}</span> a sign-in link. It expires in 15 minutes.
        </p>
      </div>
    );
  }
  return (
    <div className="max-w-md mx-auto">
      <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">My account</div>
      <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
        Sign in with your email.
      </h1>
      <p className="text-white/60 leading-relaxed mb-8">
        We&apos;ll email you a one-tap link. No password to remember. Once in, you&apos;ll see past orders, favourite
        cuts and one-tap reorder.
      </p>
      <form onSubmit={onRequest} className="space-y-4">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="account-email-input"
            required
            className="input"
            placeholder="you@example.com"
          />
        </label>
        {error && (
          <div className="border border-[var(--mw-primary)]/40 text-[var(--mw-primary)] text-xs uppercase tracking-[0.22em] px-4 py-3">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || !email}
          data-testid="account-request-link"
          className="w-full inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-60 text-white py-4 text-xs uppercase tracking-[0.22em] transition-colors"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {busy ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>
    </div>
  );
}

function AuthedView({ me, orders, subscriptions, referral, onCancelSub, onResumeSub }) {
  return (
    <div data-testid="account-authed">
      <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">My account</div>
      <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight mb-2">
        Welcome back.
      </h1>
      <div className="text-white/60 mb-10">{me?.email}</div>

      {/* Referral widget */}
      {referral && <ReferralWidget referral={referral} />}

      {/* Subscriptions */}
      {subscriptions?.length > 0 && (
        <section className="mb-14">
          <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-[0.32em] text-white/55">
            <Repeat size={13} /> Recurring deliveries ({subscriptions.length})
          </div>
          <ul data-testid="account-subscriptions" className="space-y-4">
            {subscriptions.map((s) => (
              <SubscriptionCard
                key={s.subscription_id}
                sub={s}
                onCancel={() => onCancelSub(s.subscription_id)}
                onResume={() => onResumeSub(s.subscription_id)}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Favourites */}
      {me?.favourites?.length > 0 && (
        <section className="mb-14">
          <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-[0.32em] text-white/55">
            <Star size={13} /> Your favourite cuts
          </div>
          <ul data-testid="account-favourites" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {me.favourites.map((f) => (
              <li key={f.product_id} className="border border-white/10 bg-white/[0.02] px-4 py-3">
                <div className="font-display text-lg leading-tight">{f.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">
                  {f.category} · Ordered {f.orders}×
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Orders */}
      <section>
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-[0.32em] text-white/55">
          <ShoppingBag size={13} /> Order history ({orders.length})
        </div>
        {orders.length === 0 ? (
          <div className="border border-dashed border-white/15 p-10 text-center text-white/50">
            No orders yet. <Link to="/order" className="text-[var(--mw-primary)] hover:underline ml-1">Start your first order</Link>
          </div>
        ) : (
          <ul data-testid="account-orders" className="space-y-4">
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SubscriptionCard({ sub, onCancel, onResume }) {
  const nextDate = sub.current_period_end
    ? new Date(sub.current_period_end * 1000)
    : null;
  const cancelling = sub.cancel_at_period_end;
  const totalItems = (sub.items || []).length;
  return (
    <li
      data-testid={`account-subscription-${sub.subscription_id}`}
      className={`border p-5 ${
        cancelling ? 'border-amber-500/40 bg-amber-500/[0.03]' : 'border-emerald-500/30 bg-emerald-500/[0.03]'
      }`}
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            {sub.frequency_label || sub.frequency || 'Recurring'} · {totalItems} item{totalItems === 1 ? '' : 's'}
          </div>
          <div className="font-display text-xl mt-0.5">
            {sub.items?.[0]?.name}
            {totalItems > 1 && <span className="text-white/50 text-base ml-1">+ {totalItems - 1} more</span>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
          cancelling
            ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
            : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
        }`}>
          {cancelling ? 'Cancelling' : sub.status}
        </span>
      </header>
      {nextDate && (
        <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/55">
          {cancelling ? 'Ends' : 'Next charge'}{' '}
          <span className="text-white font-medium normal-case tracking-normal">
            {nextDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          {' · '}
          <span className="text-white font-display text-base normal-case tracking-normal">{formatAUD(sub.total)}</span>
        </div>
      )}
      <footer className="mt-4 pt-4 border-t border-white/10 flex items-center justify-end gap-2">
        {cancelling ? (
          <button
            type="button"
            onClick={onResume}
            data-testid={`account-subscription-resume-${sub.subscription_id}`}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] border border-emerald-500/40 hover:border-emerald-400 hover:text-emerald-300 text-white px-4 py-2 transition-colors"
          >
            <Play size={11} /> Resume
          </button>
        ) : (
          <button
            type="button"
            onClick={onCancel}
            data-testid={`account-subscription-cancel-${sub.subscription_id}`}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] border border-white/25 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] text-white px-4 py-2 transition-colors"
          >
            <X size={11} /> Cancel at period end
          </button>
        )}
      </footer>
    </li>
  );
}

function OrderCard({ order }) {
  const { add, openDrawer } = useCart();
  const [reorderDone, setReorderDone] = useState(false);
  const paid = order.payment_status === 'paid' || order.status === 'paid';
  const date = order.created_at ? new Date(order.created_at) : null;

  const reorder = () => {
    // Fetch product data by id from a cached endpoint would be more accurate, but
    // the order line already has the price/unit/quantity — good enough for a one-tap reorder.
    order.items.forEach((it) => {
      add({
        id: it.product_id,
        name: it.name,
        category: it.category,
        price: it.original_unit_price || it.unit_price,
        unit: it.unit,
        step_qty: it.unit === 'kg' ? 0.5 : 1,
        min_qty: it.unit === 'kg' ? 0.5 : 1,
      }, Number(it.quantity));
    });
    setReorderDone(true);
    toast.success(`${order.items.length} item${order.items.length === 1 ? '' : 's'} added to cart`);
    setTimeout(() => openDrawer(), 200);
  };

  return (
    <li data-testid={`account-order-${order.id}`} className="border border-white/10 bg-white/[0.02] p-5">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            {date ? date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : order.id}
          </div>
          <div className="font-display text-xl mt-0.5">{order.id}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 ${
          paid ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
               : 'bg-white/5 text-white/60 border border-white/10'
        }`}>
          {order.payment_status || order.status}
        </span>
      </header>
      <ul className="mt-4 space-y-1 text-sm text-white/70">
        {order.items.slice(0, 4).map((it) => (
          <li key={it.product_id} className="flex justify-between">
            <span>
              {it.name} · {it.unit === 'kg' ? `${it.quantity}kg` : `x${it.quantity}`}
            </span>
            <span className="tabular-nums">{formatAUD(it.line_total)}</span>
          </li>
        ))}
        {order.items.length > 4 && (
          <li className="text-white/45 text-xs">+{order.items.length - 4} more</li>
        )}
      </ul>
      <footer className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-white/50 text-[10px] uppercase tracking-[0.22em]">
          {order.fulfilment?.label || 'Delivery'} · Total{' '}
          <span className="text-white font-display text-base normal-case tracking-normal">{formatAUD(order.total)}</span>
        </div>
        <button
          type="button"
          onClick={reorder}
          disabled={!paid}
          data-testid={`account-reorder-${order.id}`}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] border border-white/25 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 transition-colors"
        >
          {reorderDone ? <Check size={12} className="text-emerald-400" /> : <RotateCcw size={12} />}
          {reorderDone ? 'Added to cart' : 'Reorder'}
        </button>
      </footer>
    </li>
  );
}


function ReferralWidget({ referral }) {
  const [copied, setCopied] = useState(null); // 'code' | 'link' | null
  const creditDollars = ((referral.credits_available || 0) * (referral.reward_amount_dollars || 20));
  const shareText = `Get $20 off your first order at The Meat Works — use my code ${referral.code}. ${referral.share_url}`;

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      toast.success(kind === 'code' ? 'Code copied' : 'Share link copied');
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'The Meat Works — $20 off',
          text: shareText,
          url: referral.share_url,
        });
      } catch { /* user cancelled */ }
    } else {
      copy(shareText, 'link');
    }
  };

  return (
    <section
      data-testid="account-referral-widget"
      className="mb-14 border border-[var(--mw-primary)]/40 bg-gradient-to-br from-[var(--mw-primary)]/10 via-white/[0.02] to-transparent p-6 sm:p-8"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[var(--mw-primary)]/20 border border-[var(--mw-primary)]/40 flex items-center justify-center shrink-0">
          <Gift size={18} className="text-[var(--mw-primary)]" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)]">Refer a friend</div>
          <h2 className="font-display text-2xl sm:text-3xl leading-tight mt-1">
            Both save $20.
          </h2>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            Share your code. Your friend gets <span className="text-white">$20 off</span> their first order (min $50).
            You get <span className="text-white">$20 credit</span> when they check out — auto-applied to your next order.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <div className="sm:col-span-2 border border-white/15 bg-black/40 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-0.5">Your code</div>
            <div
              data-testid="referral-code"
              className="font-display text-2xl sm:text-3xl tracking-widest text-white truncate"
            >
              {referral.code}
            </div>
          </div>
          <button
            type="button"
            onClick={() => copy(referral.code, 'code')}
            data-testid="referral-copy-code"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border border-white/25 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] text-white px-3 py-2 transition-colors shrink-0"
          >
            {copied === 'code' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            {copied === 'code' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <button
          type="button"
          onClick={shareNative}
          data-testid="referral-share"
          className="inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white text-[11px] uppercase tracking-[0.22em] px-4 py-3 transition-colors"
        >
          <Gift size={14} /> Share $20 off
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div className="border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-1.5">
            <Copy size={11} /> Share link
          </div>
          <button
            type="button"
            onClick={() => copy(referral.share_url, 'link')}
            data-testid="referral-copy-link"
            className="text-[10px] uppercase tracking-[0.22em] text-[var(--mw-primary)] hover:underline truncate max-w-[200px]"
            title={referral.share_url}
          >
            {copied === 'link' ? 'Link copied' : 'Copy link'}
          </button>
        </div>
        <div
          data-testid="referral-stats"
          className={`border px-4 py-3 flex items-center justify-between gap-3 ${
            creditDollars > 0
              ? 'border-emerald-500/40 bg-emerald-500/[0.06]'
              : 'border-white/10 bg-white/[0.02]'
          }`}
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-1.5">
            <Users size={11} /> {referral.referred_count || 0} referred
          </div>
          <div
            className={`font-display text-lg ${creditDollars > 0 ? 'text-emerald-300' : 'text-white/70'}`}
            data-testid="referral-credit-available"
          >
            {creditDollars > 0 ? `$${creditDollars} credit` : 'No credit yet'}
          </div>
        </div>
      </div>

      {creditDollars > 0 && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
          ✓ Your ${creditDollars} credit will apply automatically on your next order (min $50)
        </div>
      )}
    </section>
  );
}
